<template>
  <div data-component="apps-integration-preview">
    <div class="app-info-block">
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="!app.avatar"
        class="app-avatar-default"
        :style="getAvatarStyle"
        v-html="DEFAULT_AVATAR_SVG"
      />
      <!-- eslint-enable vue/no-v-html -->

      <img
        v-if="app.avatar"
        class="app-avatar"
        :src="app.avatar"
      >

      <div class="app-info">
        <div class="app-name">
          {{ app.name }}
        </div>

        <div
          v-if="app.description"
          class="app-description"
        >
          {{ app.description }}
        </div>

        <div
          class="app-status"
          :class="{ 'is-online': app.isActive }"
        >
          {{ appStatusLabel }}
        </div>
      </div>
    </div>

    <or-input
      :model-value="app.shareUrl"
      :label="$t('HITL.app-launcher.apps-integration.app-url')"
      readonly
    />

    <or-input
      :model-value="app.shareShortUrl"
      :label="$t('HITL.app-launcher.apps-integration.app-short-url')"
      readonly
    />

    <div class="qr-code">
      <or-label>
        {{ $t('HITL.app-launcher.apps-integration.app-qr-code') }}
      </or-label>

      <div
        class="qr-code-container"
        @click="onQRCodeClick"
      >
        <img :src="qrCodeSrc">
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import {
  OrInputV3 as OrInput,
  OrLabelV3 as OrLabel,
} from '@onereach/ui-components-25';

import { OneReachApp } from '../../../types';
import { DEFAULT_AVATAR_SVG } from '../constants';

export default defineComponent({
  name: 'AppsIntegrationPreview',
  components: { OrInput, OrLabel },
  props: {
    app: {
      type: Object as PropType<OneReachApp>,
      default: () => ({}),
    },
    qrCodeUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      DEFAULT_COLOR: 'var(--or-c-on-background)',
      DEFAULT_AVATAR_SVG,
    };
  },
  computed: {
    getAvatarStyle() {
      const color = this.app.color || this.DEFAULT_COLOR;
      return { fill: `#${color}` };
    },
    appStatusLabel() {
      return this.app.isActive
        ? this.$t('HITL.common.status.online')
        : this.$t('HITL.common.status.offline');
    },
    qrCodeSrc() {
      return `data:image/svg+xml;base64,${btoa(this.qrCodeUrl)}`;
    },
  },
  methods: {
    onQRCodeClick() {
      this.$emit('qr-code-click');
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='apps-integration-preview'] {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  line-height: 1.5;

  .app-info-block {
    display: flex;
    gap: var(--s-3);

    .app-avatar-default {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--or-c-surface-1);
      border-radius: 50%;

      ::v-deep svg {
        height: 85%;
        width: 85%;
      }
    }

    .app-avatar,
    .app-avatar-default {
      flex: 0 0 auto;
      height: 3.25rem;
      width: 3.25rem;
    }

    .app-info {
      display: flex;
      flex-direction: column;

      .app-name {
        color: var(--or-c-on-background);
      }

      .app-description,
      .app-status {
        font-size: var(--fs-0);
        color: var(--or-c-outline);

        &.is-online {
          color: var(--or-c-success);
        }
      }

      .app-status {
        font-weight: var(--fw-1);
      }
    }
  }

  .qr-code {
    display: flex;
    flex-direction: column;
    gap: var(--s-2);

    .qr-code-container {
      width: fit-content;
      padding: var(--s-5);
      background: var(--or-c-surface-2);
      border-radius: var(--s-2);
      cursor: pointer;

      img {
        height: 7.5rem;
        width: 7.5rem;
        background: var(--or-c-white);
      }
    }
  }
}
</style>
